/// <reference types="google.maps" />
import React, { useContext, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SidepanelHeader from "./ui/sidepanel-header";
import { ActiveRouteDataContext } from "./ActiveRouteDataLayer";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";
import { useMap } from "@vis.gl/react-google-maps";
import { formatLastKnownTime } from "../../../utils/formatDate";

const CrewDetail = () => {
  const { activeRoutesQuery } = useContext(ActiveRouteDataContext);
  const { crewId } = useParams();
  const navigate = useNavigateKeepSearchParams(["app_version"]);
  const baseUrl = window.location.pathname!.match(/(.*active-routes)\//)![1];
  const map = useMap();

  let crewMember = null;
  for (const staff of activeRoutesQuery?.data?.staff ?? []) {
    if (staff._id.toString() === crewId) {
      crewMember = staff;
    }
  }

  useEffect(() => {
    if (crewMember?.lastKnownLocation && map) {
      let bounds = new google.maps.LatLngBounds();
      bounds.extend(crewMember.lastKnownLocation);
      map.fitBounds(bounds, 100);
      map.setZoom(15);
    }
  }, [crewMember?.lastKnownLocation, map]);

  // Handle case where crew member is not found
  if (!crewMember) {
    return (
      <div className="h-full">
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between">
            <SidepanelHeader
              onClose={(e: Event) => {
                e.preventDefault();
                navigate(`${baseUrl}/main`);
              }}
            >
              Crew Member Not Found
            </SidepanelHeader>
          </div>
        </div>
        <div className="px-4 py-5 text-center text-gray-500">
          <p>The crew member could not be found.</p>
        </div>
      </div>
    );
  }

  const crewName = `${crewMember.firstName} ${crewMember.lastName}`;
  const locationIssues =
    crewMember.lastKnownLocation == null &&
    (crewMember.session?.foregroundLocationEnabled !== true ||
      crewMember.session?.backgroundLocationEnabled !== true ||
      crewMember.session?.batteryOptimizationsDisabled !== true ||
      crewMember.session?.locationEnabled !== true);

  return (
    <div className="h-full">
      <div className="">
        <div className="px-6 pb-6">
          <div className="mb-2 flex items-center justify-between">
            <SidepanelHeader
              onClose={(e: Event) => {
                e.preventDefault();
                navigate(`${baseUrl}/main`);
              }}
            >
              {crewName}
            </SidepanelHeader>
          </div>
        </div>
      </div>
      <div className="px-4">
        <div className="text-sm text-gray-600">
          {locationIssues && (
            <div className=" bg-[#FCEDDA] border border-[#FFCF92] p-2 rounded text-black mb-2">
              <h3 className="font-bold">
                <span className="text-[#AB6103]">⚠</span> Location Issues
              </h3>
              <p>
                Location data may be poor for this crew member due to their
                phone configuration. See the details below and ask them to{" "}
                <Link to="/docs/guides/device-settings" className="underline">
                  adjust their settings
                </Link>
                .
              </p>
            </div>
          )}
          <table className="w-full">
            <tr className="">
              <th colSpan={2} className="font-bold text-left border-b pt-2">
                Crew Status
              </th>
            </tr>
            <tr>
              <th className={TH_CLASSES}>Time Clock</th>
              <td>
                <Link
                  to={`/admin/timeclock?filter[user_id]=${crewMember._id}`}
                  className="underline"
                >
                  {crewMember.clockedIn ? "On the clock" : "Clocked Out"}
                </Link>
              </td>
            </tr>
            <tr>
              <th className={TH_CLASSES}>Location</th>
              <td>
                {crewMember.lastKnownLocation
                  ? `${crewMember.lastKnownLocation.lat},${crewMember.lastKnownLocation.lng}`
                  : "Unknown"}
              </td>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Last Contact</th>
              <td>
                {formatLastKnownTime(
                  crewMember.lastKnownLocation?.createdAt ??
                    crewMember.session?.lastCheckIn,
                )}
              </td>
            </tr>
            <tr className="">
              <th colSpan={2} className="font-bold text-left border-b pt-2">
                Device Location Settings
              </th>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Allow Location</th>
              <td>
                <LocationCheck
                  state={crewMember.session?.foregroundLocationEnabled}
                  desiredState={true}
                />
                <SettingLink
                  hasIssue={
                    crewMember.session?.foregroundLocationEnabled !== true
                  }
                  docPath="/docs/guides/device-settings#allow-location"
                />
              </td>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Background Location</th>
              <td>
                <LocationCheck
                  state={crewMember.session?.backgroundLocationEnabled}
                  desiredState={true}
                />
                <SettingLink
                  hasIssue={
                    crewMember.session?.backgroundLocationEnabled !== true
                  }
                  docPath="/docs/guides/device-settings#background-location"
                />
              </td>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Battery Optimizations Disabled</th>
              <td>
                <LocationCheck
                  state={crewMember.session?.batteryOptimizationsDisabled}
                  desiredState={true}
                />
                <SettingLink
                  hasIssue={
                    crewMember.session?.batteryOptimizationsDisabled !== true
                  }
                  docPath="/docs/guides/device-settings#battery-optimizations"
                />
              </td>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Location Turned On</th>
              <td>
                <LocationCheck
                  state={crewMember.session?.locationEnabled}
                  desiredState={true}
                />
                <SettingLink
                  hasIssue={crewMember.session?.locationEnabled !== true}
                  docPath="/docs/guides/device-settings#location-enabled"
                />
              </td>
            </tr>
            <tr className="text-left">
              <th className={TH_CLASSES}>Device Type</th>
              <td>{crewMember.session?.deviceOS ?? "Unknown"}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  );
};
const SettingLink = ({
  hasIssue,
  docPath,
}: {
  hasIssue: boolean;
  docPath: string;
}) => {
  if (!hasIssue) return null;
  return (
    <a
      href={docPath}
      target="_blank"
      className="ml-2 text-blue-500 hover:text-blue-400 underline text-xs"
    >
      (help)
    </a>
  );
};
const TH_CLASSES = "font-normal text-black text-left";
function LocationCheck({
  state,
  desiredState,
}: {
  state: unknown;
  desiredState: unknown;
}) {
  if (state === desiredState) {
    return <span className="text-snow-green">✓ Ok</span>;
  }
  if (state === null || state === undefined) {
    return "Unknown";
  }
  return <span className="text-snow-red">⚠&nbsp;Issue</span>;
}

export default CrewDetail;
